import { withAITracking } from '@microsoft/applicationinsights-react-js';
import FileSaver from 'file-saver';
import { FC, useCallback, useEffect, useMemo, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsFeatureEnabled } from '../../api/features/features';
import {
    getGetAmountsEntriesQueryKey,
    useExportReportCellAuditToExcel,
    useExportReportRowTraceToExcel,
    useGetReportCellAudit,
    useSaveAmounts,
} from '../../api/report/report';
import { applicationInsightsReactPlugin } from '../../ApplicationInsightsService';
import { IDrillDownValues } from '../../contexts/drillDownContext';

import { IUnitTestProps } from '../../data-types/IUnitTestProps';
import { SelectedWhitePaperCell } from '../../data-types/selectedWhitePaperCell';
import { useDrillDown } from '../../hooks/useDrillDown';
import { useDrillDownStyles } from '../../hooks/useDrillDownStyles';
import { useFunctionalAccess } from '../../hooks/useFunctionalAccess';
import {
    getNumberOfAccountGroupsWithAdjustments,
    getNumberOfUniqueAccounts,
    IReportDrillDownLoadedTrackingInformation,
    IReportDrilldownSavedTrackingInformation,
    usePerformanceMetricsForDrillDown,
} from '../../hooks/usePerformanceMetricsForDrillDown';
import {
    ExtractionDetailsAccountSummary,
    ExtractionDetailsAdjustmentInfo,
    ExtractionDetailsRequest,
    IWhitePaperColumn,
    IWhitePaperMappedRow,
    IWhitePaperMappedRowDictionary,
    IWhitePaperReportChanges,
    ProblemDetails,
    Report,
    ReportCellAuditExportRequest,
    ReportCellAuditRequest,
    ReportRowTraceExportRequest,
    SaveAmountsRequest,
    SaveAmountsResponse,
} from '../../model';

import {
    Button,
    DialogTrigger,
    SelectTabData,
    SelectTabEvent,
    Spinner,
    Tab,
    TabList,
    TreeItemOpenChangeData,
    TreeItemOpenChangeEvent,
    TreeOpenChangeData,
    TreeOpenChangeEvent,
} from '@fluentui/react-components';
import { AddCircle20Regular, ArrowDownload20Regular, SubtractCircle20Regular } from '@fluentui/react-icons';
import { useQueryClient } from '@tanstack/react-query';
import { getGetJournalEntriesQueryKey } from '../../api/amount/amount';
import { usePreventNavigation } from '../../hooks/usePreventNavigation';
import {
    DrillDownStateReducer,
    drillDownStateReducer,
    IDrillDownState,
    IErrorMessage,
    IExportButtonState,
    IRowChangeSummary,
} from '../../reducers/drillDownStateReducer';
import { ENTRY_POINT_DIALOG_CLOSE } from '../../telemetry/entryPoints';
import { ACTION_INVOKED_EVENT, REPORT_DRILLDOWN_CLOSED } from '../../telemetry/events';
import { useEventTracker } from '../../telemetry/hooks/useEventTracker';
import { mergeChangeTrackingData } from '../../utils/ChangeTrackingDataUtils';
import { FilteredEnterAmountsEnabled } from '../../utils/FeatureFlags.constants';
import { ErrorType } from '../../utils/UseCustomInstance';
import { ToggleWidthDrawer } from '../common/ToggleWidthDrawer';
import ConfirmationDialogV9 from '../dialog/ConfirmationDialogV9';
import EnterAmountsDialog from '../enter-amounts-dialog/EnterAmountsDialog';
import AccountAmountSection from './AccountAmountSection';
import { AccountAmountTabPanel } from './AccountAmountTabPanel/';
import { JournalEntryDialog } from './JournalEntryDialog';
import ReportColumnContext from './ReportColumnContext';
import { DrillDownActionType } from './ReportDrillDownActions';
import {
    AccountAmountWhitePaperRow,
    buildExtractionRequest,
    consolidateAmountChangesForSave,
    getDefiningRowsFromDataSource,
    getImpactedRowsFromDataSource,
    getSelectedRowFromDataSource,
    getUniqueExtractionDetailRequests,
    ITrackedExtractionDetailsAccountSummary,
    IWhitePaperRowTrace,
} from './ReportDrillDownUtils';
import TraceCalculationTabPanel from './TraceCalculationTabPanel';

export interface IReportDrillDownOnClose {
    amountsChangedOnServer: boolean;
    reportChanges: IWhitePaperReportChanges;
}
export interface IReportDrillDownProps extends IUnitTestProps {
    onClose: (changeTrackingData: IReportDrillDownOnClose) => void;
    showPanel: boolean;
    report: Report;
    whitePaperReportMap: IWhitePaperMappedRowDictionary;
    whitePaperReportColumn: IWhitePaperColumn | undefined;
    changeTrackingData: IWhitePaperReportChanges;
    isMultiColumnWhitePaperReport?: boolean;
}

enum PostSaveChangesBehavior {
    SaveOnly = 0,
    SaveAndClose = 1,
}

function assignAccountSummaryEphemeralIds(summaries: ExtractionDetailsAccountSummary[]): ITrackedExtractionDetailsAccountSummary[] {
    let ephemeralKey: number = 0;

    for (const summary of Object.values<any>(summaries)) {
        summary.ephemeralKey = ++ephemeralKey;
    }

    return summaries as ITrackedExtractionDetailsAccountSummary[];
}

function handleExportButtonClick(selectedPivotIndex: number, exportFunctionMap: Map<number, () => void>) {
    if (!exportFunctionMap) {
        return;
    }
    const exportFunction: (() => void) | undefined = exportFunctionMap.get(selectedPivotIndex);

    if (exportFunction) {
        exportFunction();
    }
}

const customizedTabListId: string = 'customizedTabList';
const accountAmountTabId: string = 'accountAmountTab';
const traceCalculationTabId: string = 'traceCalculationTab';
const drilldownGridCollapseButtonId: string = 'drilldownGridCollapseButton';
const drilldownExportButtonId: string = 'drilldownExportButton';
const reportDrillDownTabContainerId: string = 'reportDrillDownTabContainer';

const ReportDrillDown: FC<IReportDrillDownProps> = (props: IReportDrillDownProps) => {
    const { blocker, setShouldBlockNavigation } = usePreventNavigation();
    const queryClient = useQueryClient();
    const { data: isFilteredEnterAmountsEnabled } = useIsFeatureEnabled({ featureFlagName: FilteredEnterAmountsEnabled });
    const { trackDrilldownLoading, trackDrilldownLoaded, trackDrilldownSaving, trackDrilldownSaved } = usePerformanceMetricsForDrillDown();
    const { trackEvent } = useEventTracker();
    const { t } = useTranslation();
    const styles = useDrillDownStyles();
    const { data: isJournalEntryEnabled } = useIsFeatureEnabled({ featureFlagName: 'JournalEntryEnabled' });

    const expandText: string = t('expand');
    const collapseText: string = t('collapse');
    const accountAmountTabText: string = t('accountAmount').toString();
    const traceCalculationTabText: string = t('traceCalculation').toString();
    const headerText: string = t('reportDrillDown').toString();
    const drillDownCtxData: IDrillDownValues = useDrillDown();
    const whitePaperCellDataRowIndex = 1;
    const whitePaperReportMap: IWhitePaperMappedRowDictionary = props.whitePaperReportMap;

    const selectedCell: SelectedWhitePaperCell = drillDownCtxData.traceCalculationData![whitePaperCellDataRowIndex];
    const definingRows: IWhitePaperRowTrace[] = getDefiningRowsFromDataSource(selectedCell, whitePaperReportMap);
    const rowsImpacted: IWhitePaperRowTrace[] = getImpactedRowsFromDataSource(selectedCell, whitePaperReportMap);
    const totalRow: IWhitePaperRowTrace = getSelectedRowFromDataSource(selectedCell, whitePaperReportMap);

    const initialDrillDownState: IDrillDownState = {
        activeTabIndex: drillDownCtxData.selectedTabKey,
        exportButtonStateMap: new Map<number, IExportButtonState>([
            [0, { onClick: exportAccountAmount, isProcessing: false } as IExportButtonState],
            [1, { onClick: exportTraceCalculationTab, isProcessing: false } as IExportButtonState],
        ]),
        showConfirmationDialog: false,
        currentAccountAmountSummaries: [] as ITrackedExtractionDetailsAccountSummary[],
        isInitializing: true,
        errorMessages: [] as IErrorMessage[],
        isServerError: false,
        rows: null,
        isFetchingOrSavingData: false,
        amountsChangedOnServer: false,
        accountAmountGridHasData: false,
        changeTrackingData: props.changeTrackingData,
        updatedChangeTrackingData: {},
        updatedChangeTrackingDataMap: new Map<string, IRowChangeSummary>(),
        openAccountSummaryItems: [] as Iterable<string>,
        openTraceCalculationItems: new Map<string, IWhitePaperRowTrace[]>(),
        definingRows: definingRows,
        rowsImpacted: rowsImpacted,
        totalRow: totalRow,
    } as IDrillDownState;
    const [drillDownState, updateDrillDownState] = useReducer<DrillDownStateReducer>(drillDownStateReducer, initialDrillDownState);

    // migrated from AccountAmountTabPanel
    const showConfirmationDialog: boolean = drillDownState.showConfirmationDialog;
    const currentAccountAmountSummaries = drillDownState.currentAccountAmountSummaries;
    const isFetchingOrSavingData = drillDownState.isFetchingOrSavingData;
    const errorMessages = drillDownState.errorMessages;
    const isServerError = drillDownState.isServerError;
    const { mutate: saveAmounts } = useSaveAmounts();
    const { mutate: getReportCellAudit } = useGetReportCellAudit();
    const { functionalAccess } = useFunctionalAccess();
    const isSaveButtonDisabled = currentAccountAmountSummaries?.length < 1 || isFetchingOrSavingData;
    const isDataLoaded = !!drillDownState.rows;

    const accountAmountGridHasData: boolean = drillDownState.rows === null ? false : drillDownState.rows.length > 0;
    const expandCollapseAllButtonText: string = useMemo(() => {
        if (drillDownCtxData.selectedTabKey === 0) {
            if (Array.from(drillDownState.openAccountSummaryItems).length <= 0) {
                return expandText;
            }
        }

        return collapseText;
    }, [expandText, collapseText, drillDownState.openAccountSummaryItems, drillDownCtxData.selectedTabKey]);

    const expandCollapseAllButtonDisabled: boolean = useMemo(() => {
        if (drillDownState.activeTabIndex === 0) {
            return !accountAmountGridHasData;
        } else {
            // For TraceCalculation tab, enable if there are expanded items
            return !drillDownState.openTraceCalculationItems?.size;
        }
    }, [drillDownState.activeTabIndex, accountAmountGridHasData, drillDownState.openTraceCalculationItems]);

    const amountsChangedOnServer: boolean = drillDownState.amountsChangedOnServer;
    const reportChanges: IWhitePaperReportChanges = drillDownState.changeTrackingData;

    const onAmountCellValueChange = useCallback(
        (event: React.FocusEvent<HTMLDivElement>): void => {
            const target = event.target as HTMLElement | null;
            if (!target) return;
            // find closest TreeItem element which renders data-fui-tree-item-value attribute
            const closestRowElement: HTMLElement | null = (target as HTMLElement).closest('[data-fui-tree-item-value]');
            const uiKey = closestRowElement?.getAttribute('data-fui-tree-item-value');
            if (!uiKey) return;
            let row: AccountAmountWhitePaperRow | null = null;
            for (const i in drillDownState.rows ?? []) {
                const root: AccountAmountWhitePaperRow = drillDownState.rows![Number(i)];
                const found: AccountAmountWhitePaperRow | null = root.findByUiKey(uiKey);
                if (found) {
                    row = found;
                    break;
                }
            }
            if (!row) {
                console.warn(`Unable to locate AccountAmountWhitePaperRow for uiKey: ${uiKey}`);
                return;
            }
            // mark outer page as dirty when any amount is edited
            setShouldBlockNavigation(true);
            updateDrillDownState({
                type: DrillDownActionType.AmountEdited,
                event: event.nativeEvent ?? (event as any),
                item: row,
                rowNumber: Number.parseInt(selectedCell.displayRowNumber!),
                columnId: props.whitePaperReportColumn!.originalColumnId!,
                report: props.report,
            });
        },
        [updateDrillDownState, drillDownState.rows, selectedCell, props.whitePaperReportColumn, props.report, setShouldBlockNavigation]
    );

    const onEnterAmountsClick = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
            const eventTargetElement = event.target;
            const closestRowElement: HTMLElement = (eventTargetElement as HTMLElement).closest('[data-fui-tree-item-value]')!;
            const uiKey = closestRowElement.getAttribute('data-fui-tree-item-value');
            let row: AccountAmountWhitePaperRow | null = null;
            for (const i in drillDownState.rows!) {
                const root: AccountAmountWhitePaperRow = drillDownState.rows![Number(i)];
                const found: AccountAmountWhitePaperRow | null = root.findByUiKey(uiKey!);
                if (found) {
                    row = found;
                    break;
                }
            }
            if (!row) {
                throw new Error(`Unable to locate AccountAmountWhitePaperRow for uiKey: ${uiKey}`);
            }
            updateDrillDownState({
                type: DrillDownActionType.EnterAmountsDialogOpened,
                enterAmountsDialogProps: {
                    buttonTrigger: event.target as HTMLElement,
                    case: row.dataObject.case,
                    jurisdiction: row.dataObject.jurisdiction ?? row.dataObject.location,
                    period: row.dataObject.year,
                    entity: row.dataObject.corporation,
                    adjustmentCode: row.dataObject.adjustmentCode ?? row.parent?.adjustmentCode,
                    location: row.dataObject.location,
                    definingRowNumber: Number.parseInt(row.rowNumber),
                    isInputRow: true,
                },
                summaryKey: row.uiKey!,
            });
        },
        [updateDrillDownState, drillDownState.rows]
    );

    const onJournalEntryClick = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
            const eventTargetElement = event.target;
            const closestRowElement: HTMLElement = (eventTargetElement as HTMLElement).closest('[data-fui-tree-item-value]')!;
            const uiKey = closestRowElement.getAttribute('data-fui-tree-item-value');
            let row: AccountAmountWhitePaperRow | null = null;
            for (const i in drillDownState.rows!) {
                const root: AccountAmountWhitePaperRow = drillDownState.rows![Number(i)];
                const found: AccountAmountWhitePaperRow | null = root.findByUiKey(uiKey!);
                if (found) {
                    row = found;
                    break;
                }
            }
            if (!row) {
                throw new Error(`Unable to locate AccountAmountWhitePaperRow for uiKey: ${uiKey}`);
            }
            updateDrillDownState({
                type: DrillDownActionType.JournalEntryDialogOpened,
                journalEntryDialogProps: {
                    buttonTrigger: event.target as HTMLElement,
                    onClose: () => {
                        updateDrillDownState({ type: DrillDownActionType.JournalEntryDialogClosed });
                    },
                    rowDescription: `${t('entity')}: ${row.entity ?? ''} ${t('case')}: ${row.case ?? ''} ${t('jurisdiction')}: ${
                        row.jurisdiction ?? ''
                    } ${t('year')}: ${row.year ?? ''}`,
                    accountCode: row.account! ?? '',
                    adjustmentCode: '',
                    caseCode: row.case!,
                    jurisdiction: row.jurisdiction!,
                    entityCode: row.entity ?? '',
                    period: row.year ?? '',
                },
            });
        },
        [updateDrillDownState, drillDownState.rows]
    );

    const rows = drillDownState.rows;
    const onAccountAmountExpandChange: (event: TreeOpenChangeEvent, data: TreeOpenChangeData) => void = useCallback(
        (_: TreeOpenChangeEvent, data: TreeOpenChangeData) => {
            updateDrillDownState({ type: DrillDownActionType.AccountAmountExpandChanged, openIds: data.openItems });
        },
        [updateDrillDownState]
    );
    const onTraceCalculationExpandChange: (event: TreeItemOpenChangeEvent, data: TreeItemOpenChangeData) => void = useCallback(
        (_, data) => {
            const uiKey = data.value.toString();
            const isOpening = data.open;

            updateDrillDownState({
                type: DrillDownActionType.TraceCalculationExpandChanged,
                uiKey,
                isOpening,
            });
        },
        [updateDrillDownState]
    );
    const traceCalculationTabPanel = useMemo(() => {
        const selectedCell = drillDownCtxData.traceCalculationData[whitePaperCellDataRowIndex];

        return (
            <TraceCalculationTabPanel
                definingRows={drillDownState.definingRows ?? []}
                totalRow={drillDownState.totalRow ?? {}}
                rowsImpacted={drillDownState.rowsImpacted ?? []}
                isUnitTestExecution={props.isUnitTestExecution}
                onOpenChange={onTraceCalculationExpandChange}
                expandedChildren={drillDownState.openTraceCalculationItems}
                key={`TraceCalculationTabPanel ${selectedCell?.displayRowNumber || 'unknown'}`}
            />
        );
    }, [
        drillDownState.definingRows,
        drillDownState.rowsImpacted,
        drillDownState.totalRow,
        drillDownState.openTraceCalculationItems,
        drillDownCtxData.traceCalculationData,
        whitePaperCellDataRowIndex,
        updateDrillDownState,
        onTraceCalculationExpandChange,
        props.isUnitTestExecution,
    ]);

    const onTabSelect = useCallback(
        (_: SelectTabEvent, data: SelectTabData) => {
            const tabIndex = Number(data.value);
            if (tabIndex < 0) {
                return;
            }
            drillDownCtxData.selectedTabKey = tabIndex;
            updateDrillDownState({
                type: DrillDownActionType.ChangeActiveTabIndex,
                newActiveTab: tabIndex,
                eventTargetTabIndex: tabIndex,
            });
        },
        [drillDownCtxData, updateDrillDownState]
    );
    const onSaveButtonClick: (postSaveChangesBehavior: PostSaveChangesBehavior) => void = useCallback(
        (postSaveChangesBehavior: PostSaveChangesBehavior) => {
            updateDrillDownState({ type: DrillDownActionType.StartProcessingSaveAmounts });
            trackDrilldownSaving();
            try {
                const consolidateAmountChanges: ExtractionDetailsAccountSummary[] =
                    consolidateAmountChangesForSave(currentAccountAmountSummaries);
                const mergedChangeTrackingData = mergeChangeTrackingData(
                    drillDownState.changeTrackingData,
                    drillDownState.updatedChangeTrackingData
                );
                const realGroupsToSave: SaveAmountsRequest = {
                    accountSummaryUpdates: consolidateAmountChanges,
                    changesTrackingData: mergedChangeTrackingData,
                    taxReturnKey: props.report.id!,
                };
                //set flag here
                saveAmounts(
                    {
                        data: realGroupsToSave,
                    },
                    {
                        onSettled: (
                            _data: SaveAmountsResponse | undefined,
                            error: ErrorType<ProblemDetails> | null,
                            variables: { data: SaveAmountsRequest },
                            _context: unknown | undefined
                        ) => {
                            const numberOfAccountsSaved: number = variables.data.accountSummaryUpdates!.length;
                            const numberOfAmountsSaved: number = variables.data
                                .accountSummaryUpdates!.filter((x: ExtractionDetailsAccountSummary) => x.adjustmentInfo)
                                .flatMap((x: ExtractionDetailsAccountSummary) => x.adjustmentInfo!)
                                .flatMap((x: ExtractionDetailsAdjustmentInfo) => x.entries).length;
                            const drillDownTrackingSaved: IReportDrilldownSavedTrackingInformation = {
                                numberOfAmountsSaved: numberOfAmountsSaved,
                                reportId: props.report?.id?.toString() ?? 'N/A',
                                isMultiColumnReport: props.isMultiColumnWhitePaperReport || false,
                                reportIsForEntityGroup: props.report.isForEntityGroup!,
                                success: error ? false : true,
                                columnId: props.whitePaperReportColumn?.originalColumnId?.toString() ?? 'N/A',
                                numberOfAccountGroups: numberOfAccountsSaved,
                                rowNumber: selectedCell.displayRowNumber!,
                            };
                            trackDrilldownSaved(drillDownTrackingSaved);

                            if (!error && postSaveChangesBehavior === PostSaveChangesBehavior.SaveAndClose && props.onClose) {
                                // we can't use the state variable directly because it won't be updated until the next re-render,
                                // at which point this component will be closed anyway
                                trackEvent(ACTION_INVOKED_EVENT, {
                                    action_name: REPORT_DRILLDOWN_CLOSED,
                                    entry_point: ENTRY_POINT_DIALOG_CLOSE,
                                });
                                props.onClose({ amountsChangedOnServer: true, reportChanges: variables.data.changesTrackingData! });
                            } else {
                                updateDrillDownState({ type: DrillDownActionType.SaveAmountsComplete });
                            }
                        },
                        onSuccess: (
                            _data: SaveAmountsResponse | undefined,
                            variables: { data: SaveAmountsRequest },
                            _context: unknown | undefined
                        ) => {
                            queryClient.invalidateQueries({ queryKey: getGetAmountsEntriesQueryKey() });
                            queryClient.invalidateQueries({ queryKey: getGetJournalEntriesQueryKey() });
                            updateDrillDownState({
                                type: DrillDownActionType.SaveAmountsSuccess,
                                updatedChangeTrackingData: variables.data.changesTrackingData!,
                            });
                            // allow navigation after successful save
                            if (blocker.state === 'blocked') {
                                blocker.proceed?.();
                            }
                            setShouldBlockNavigation(false);
                        },
                        onError: (
                            error: ErrorType<ProblemDetails> | null,
                            _variables: { data: SaveAmountsRequest },
                            _context: unknown | undefined
                        ) => {
                            updateDrillDownState({ type: DrillDownActionType.SaveAmountsFailure, error });
                        },
                    }
                );
            } catch (error: any) {
                if (error) {
                    throw new Error(error?.request?.response);
                }
            }
        },
        [updateDrillDownState, props.onClose, currentAccountAmountSummaries, saveAmounts, setShouldBlockNavigation]
    );
    const showConfirmationDialogOnExit: boolean = drillDownState.showConfirmationDialogOnExit;
    const onDrillDownClose: () => void = useCallback(() => {
        if (showConfirmationDialogOnExit) {
            updateDrillDownState({ type: DrillDownActionType.ConfirmationDialogOpened });
        } else if (props.onClose) {
            trackEvent(ACTION_INVOKED_EVENT, { action_name: REPORT_DRILLDOWN_CLOSED, entry_point: ENTRY_POINT_DIALOG_CLOSE });
            if (blocker.state === 'blocked') {
                blocker.proceed?.();
            }
            setShouldBlockNavigation(false);
            props.onClose({ amountsChangedOnServer, reportChanges });
        }
    }, [
        currentAccountAmountSummaries,
        props.onClose,
        amountsChangedOnServer,
        reportChanges,
        updateDrillDownState,
        showConfirmationDialogOnExit,
        blocker,
    ]);
    // Show confirmation dialog when navigation is blocked
    useEffect(() => {
        if (blocker && blocker.state === 'blocked' && showConfirmationDialogOnExit) {
            updateDrillDownState({ type: DrillDownActionType.ConfirmationDialogOpened });
        }
    }, [blocker, showConfirmationDialogOnExit]);
    const rowDefinition: IWhitePaperMappedRow = props.whitePaperReportMap[selectedCell.displayRowNumber!];
    const traces: IWhitePaperRowTrace[] = drillDownState.definingRows ?? [];

    const requestCellAudit = () => {
        const requests: ExtractionDetailsRequest[] = buildExtractionRequest(
            traces,
            rowDefinition,
            props.whitePaperReportColumn,
            props.report.id!
        );
        updateDrillDownState({
            type: DrillDownActionType.RequestCellAuditStart,
            extractionDetailsRequests: requests,
        });

        if (requests.length === 0) {
            const drillDownTrackingNoAttemptMade: IReportDrillDownLoadedTrackingInformation = {
                reportId: props.report?.id?.toString() ?? 'N/A',
                numberOfAccountGroups: 0,
                numberOfAccountGroupsWithAdjustments: 0,
                rowMapItemCount: Object.keys(props.whitePaperReportMap).length,
                success: 'Not Attempted',
                rowRequestCount: 0,
                isMultiColumnReport: props.isMultiColumnWhitePaperReport || false,
                reportIsForEntityGroup: false,
                numberOfUniqueAccounts: 0,
                columnId: props.whitePaperReportColumn?.originalColumnId?.toString() ?? 'N/A',
                rowNumber: selectedCell.displayRowNumber!,
            };
            trackDrilldownLoaded(drillDownTrackingNoAttemptMade);
            updateDrillDownState({ type: DrillDownActionType.RequestCellAuditEnd });
            return;
        }

        const extractionDetailRequest: ExtractionDetailsRequest[] = getUniqueExtractionDetailRequests(requests);
        const reportCellAuditRequest: ReportCellAuditRequest = {
            context: {
                case: props.report.case,
                entity: props.whitePaperReportColumn?.entityCode,
                jurisdiction: props.report.jurisdiction,
                location: props.report.location,
                year: props.report.year ?? undefined,
            },
            extractionDetailsRequests: extractionDetailRequest,
        };

        try {
            getReportCellAudit(
                {
                    data: reportCellAuditRequest,
                },
                {
                    onSettled: (
                        data: ExtractionDetailsAccountSummary[] | undefined,
                        error: ErrorType<ProblemDetails> | null,
                        _variables: { data: ReportCellAuditRequest },
                        _context: unknown | undefined
                    ) => {
                        const drilldownLoadedTracking: IReportDrillDownLoadedTrackingInformation = {
                            reportId: props.report?.id?.toString() ?? 'N/A',
                            numberOfAccountGroups: data?.length ?? 0,
                            numberOfAccountGroupsWithAdjustments: getNumberOfAccountGroupsWithAdjustments(data),
                            rowMapItemCount: Object.keys(props.whitePaperReportMap).length,
                            isMultiColumnReport: props.isMultiColumnWhitePaperReport || false,
                            success: error ? false : true,
                            rowRequestCount: extractionDetailRequest.length,
                            reportIsForEntityGroup: props.report!.isForEntityGroup!,
                            numberOfUniqueAccounts: getNumberOfUniqueAccounts(data),
                            columnId: props.whitePaperReportColumn?.originalColumnId?.toString() ?? 'N/A',
                            rowNumber: selectedCell.displayRowNumber!,
                        };
                        trackDrilldownLoaded(drilldownLoadedTracking);
                        updateDrillDownState({ type: DrillDownActionType.RequestCellAuditEnd });
                    },
                    onSuccess: (data: ExtractionDetailsAccountSummary[]) => {
                        updateDrillDownState({
                            type: DrillDownActionType.RequestCellAuditSuccess,
                            initialAccountAmountSummaries: assignAccountSummaryEphemeralIds(data),
                            report: props.report,
                            rowNumber: Number.parseInt(selectedCell.displayRowNumber!),
                            columnId: props.whitePaperReportColumn!.originalColumnId!,
                        });
                    },
                    onError: (error: ErrorType<ProblemDetails>) => {
                        updateDrillDownState({ type: DrillDownActionType.RequestCellAuditFailure, error });
                    },
                }
            );
        } catch (error: any) {
            if (error) {
                throw new Error(error?.request?.response);
            }
        }
    };

    const renderErrorMessages = () => {
        return (
            <ul>
                {errorMessages?.map((message) => {
                    const messageContent: string = message.needsTranslation
                        ? t(message.message, message.translationArgs).toString()
                        : message.message;
                    return <li key={messageContent}>{messageContent}</li>;
                })}
            </ul>
        );
    };

    // make sure we only do this once
    if (drillDownState.isInitializing) {
        trackDrilldownLoading();
        requestCellAudit();
    }

    function onConfirmationModalSubmit(_event: React.MouseEvent<HTMLElement, MouseEvent>): void {
        onSaveButtonClick(PostSaveChangesBehavior.SaveAndClose);
    }

    const exportTraceCalcHook = useExportReportRowTraceToExcel();
    const exportAccountAmountHook = useExportReportCellAuditToExcel();
    function exportTraceCalculationTab() {
        updateDrillDownState({ type: DrillDownActionType.StartProcessingExport, eventTargetTabIndex: 1 });
        const reportRowTraceRequest: ReportRowTraceExportRequest = {
            columnFieldName: props.whitePaperReportColumn?.fieldName,
            rowNumber: selectedCell.displayRowNumber,
            reportId: props.report.id,
            reportName: props.report.name,
            period: props.report.period,
            executedDate: props.report.executed,
        } as ReportRowTraceExportRequest;

        exportTraceCalcHook.mutate(
            { data: reportRowTraceRequest },
            {
                onSuccess: (data: Blob) => {
                    FileSaver.saveAs(
                        data,
                        `TraceCalculation - ${props.report.name} - Row ${rowDefinition.rowNumber} - ${new Date().toISOString()}.xlsx`
                    );
                },
                onError: (_error: ErrorType<ProblemDetails>) => {
                    updateDrillDownState({ type: DrillDownActionType.ExportFailure, eventTargetTabIndex: 1 });
                },
                onSettled: (
                    _data: Blob | undefined,
                    _error: ErrorType<ProblemDetails> | null,
                    _variables: { data: ReportRowTraceExportRequest },
                    _context: unknown | undefined
                ) => {
                    updateDrillDownState({ type: DrillDownActionType.StopProcessingExport, eventTargetTabIndex: 1 });
                },
            }
        );
    }
    function exportAccountAmount() {
        updateDrillDownState({ type: DrillDownActionType.StartProcessingExport, eventTargetTabIndex: 0 });
        const reportCellAuditRequest = {
            extractionDetailsRequests: buildExtractionRequest(traces, rowDefinition, props.whitePaperReportColumn, props.report.id!),
            entityName: props.whitePaperReportColumn?.entityName,
            reportName: props.report.name,
            executedAt: props.report.executed,
            period: props.report.period,
        } as ReportCellAuditExportRequest;
        exportAccountAmountHook.mutate(
            { data: reportCellAuditRequest },
            {
                onSuccess: (data: Blob) => {
                    FileSaver.saveAs(
                        data,
                        `AccountAmountExport - ${props.report.name} - Row ${rowDefinition.rowNumber} - ${new Date().toISOString()}.xlsx`
                    );
                },
                onError: (_error: ErrorType<ProblemDetails>) => {
                    updateDrillDownState({ type: DrillDownActionType.ExportFailure, eventTargetTabIndex: 0 });
                },
                onSettled: (
                    _data: Blob | undefined,
                    _error: ErrorType<ProblemDetails> | null,
                    _variables: { data: ReportCellAuditRequest },
                    _context: unknown | undefined
                ) => {
                    updateDrillDownState({ type: DrillDownActionType.StopProcessingExport, eventTargetTabIndex: 0 });
                },
            }
        );
    }

    const exportFunctionMap = useMemo(
        () =>
            new Map<number, () => void>([
                [0, exportAccountAmount],
                [1, exportTraceCalculationTab],
            ]),
        [exportAccountAmount, exportTraceCalculationTab]
    );

    const expandAllClickHandler = useCallback(() => {
        if (drillDownCtxData.selectedTabKey === 0) {
            return !Array.from(drillDownState.openAccountSummaryItems).length
                ? updateDrillDownState({
                      type: DrillDownActionType.AccountAmountExpandChanged,
                      openIds: new Set<string | number>(rows!.map((row) => row.uiKey!)),
                  })
                : updateDrillDownState({ type: DrillDownActionType.AccountAmountExpandChanged, openIds: new Set() });
        } else {
            return updateDrillDownState({ type: DrillDownActionType.TraceCalculationExpandChanged });
        }
    }, [
        drillDownCtxData.selectedTabKey,
        expandCollapseAllButtonText,
        expandText,
        updateDrillDownState,
        rows,
        drillDownState.openAccountSummaryItems,
        whitePaperCellDataRowIndex,
        props.whitePaperReportColumn,
        whitePaperReportMap,
    ]);
    const exportClickHandler = useCallback(
        () => handleExportButtonClick(drillDownState.activeTabIndex, exportFunctionMap),
        [drillDownState, exportFunctionMap]
    );
    const confirmationDialogCancelButtonHandler = useCallback(
        (_event: React.MouseEvent<HTMLElement>) => {
            trackEvent(ACTION_INVOKED_EVENT, { action_name: REPORT_DRILLDOWN_CLOSED, entry_point: ENTRY_POINT_DIALOG_CLOSE });
            if (blocker.state === 'blocked') {
                blocker.proceed?.();
            }
            setShouldBlockNavigation(false);
            props!.onClose({ amountsChangedOnServer, reportChanges: reportChanges });
        },
        [props.onClose, amountsChangedOnServer, reportChanges, trackEvent, blocker]
    );
    const confirmationDialogOnDismissHandler = useCallback(() => {
        updateDrillDownState({ type: DrillDownActionType.ConfirmationDialogClosed });
        // here we don't close or navigate away, we just need to reset the blocker
        blocker.reset?.();
    }, [updateDrillDownState, blocker]);
    const panelOnConfirmHandler = useCallback(() => onSaveButtonClick(PostSaveChangesBehavior.SaveOnly), [onSaveButtonClick]);

    const onEnterAmountsClosedCallback: (amountsChangedOnServer: boolean, changeTrackingData: IWhitePaperReportChanges) => void =
        useCallback(
            (amountsChangedOnServer: boolean, changeTrackingData: IWhitePaperReportChanges) => {
                updateDrillDownState({
                    type: DrillDownActionType.EnterAmountsDialogClosed,
                    updatedChangeTrackingData: changeTrackingData,
                    amountsChangedOnServer: amountsChangedOnServer,
                });
            },
            [drillDownState, updateDrillDownState]
        );
    const showEnterAmountsRowDescription =
        drillDownState.enterAmountsDialogProps?.adjustmentCode ||
        (drillDownState.enterAmountsDialogProps?.isInputRow && isFilteredEnterAmountsEnabled);
    const isProcessing = drillDownState.exportButtonStateMap.get(drillDownState.activeTabIndex)?.isProcessing;
    return (
        <ToggleWidthDrawer
            isOpen={props.showPanel}
            onClose={onDrillDownClose}
            onConfirm={panelOnConfirmHandler}
            primaryButtonDisabled={isSaveButtonDisabled}
            headerText={headerText}
        >
            <div id='drilldownHeader' className={styles.header}>
                <AccountAmountSection data={selectedCell} />
                <ReportColumnContext
                    case={props.report.case ?? ''}
                    entity={props.whitePaperReportColumn?.entityCode ?? ''}
                    jurisdiction={props.report.jurisdiction ?? ''}
                    year={props.report.year?.toString() ?? ''}
                />
                {isServerError && (
                    <div className={styles.errorMessage}>
                        <p>{t('dataLoadError')}</p>
                        <p>{t('retryOrContactAdmin')}</p>
                        {renderErrorMessages()}
                    </div>
                )}
                {!isServerError && (errorMessages?.length ?? 0) > 0 && <div className={styles.errorMessage}>{renderErrorMessages()}</div>}
            </div>
            <div id={reportDrillDownTabContainerId} className={styles.drillDownTabPanelContainer}>
                <div id={customizedTabListId} className={styles.tabBarRow}>
                    <TabList
                        className={styles.tabList}
                        aria-label={headerText}
                        selectedValue={!props.isUnitTestExecution ? drillDownState.activeTabIndex : undefined}
                        defaultSelectedValue={props.isUnitTestExecution ? drillDownState.activeTabIndex : undefined}
                        onTabSelect={onTabSelect}
                    >
                        <Tab id={accountAmountTabId} aria-label={accountAmountTabText} className={styles.tabButton} value={0}>
                            {accountAmountTabText}
                        </Tab>
                        <Tab id={traceCalculationTabId} aria-label={traceCalculationTabText} className={styles.tabButton} value={1}>
                            {traceCalculationTabText}
                        </Tab>
                    </TabList>
                    <Button
                        size='small'
                        aria-label={expandCollapseAllButtonText}
                        disabled={expandCollapseAllButtonDisabled}
                        aria-disabled={expandCollapseAllButtonDisabled}
                        id={drilldownGridCollapseButtonId}
                        onClick={expandAllClickHandler}
                        icon={
                            expandCollapseAllButtonText === expandText ? (
                                <AddCircle20Regular className={styles.icon} />
                            ) : (
                                <SubtractCircle20Regular className={styles.icon} />
                            )
                        }
                    >
                        {expandCollapseAllButtonText}
                    </Button>
                    <Button
                        id={drilldownExportButtonId}
                        size='small'
                        onClick={exportClickHandler}
                        icon={<ArrowDownload20Regular className={styles.icon} />}
                        disabled={isProcessing}
                        aria-disabled={isProcessing}
                        aria-label={isProcessing ? t('loading').toString() : t('export').toString()}
                    >
                        {isProcessing ? (
                            <>
                                {t('loading').toString()}
                                <Spinner className={styles.spinnerIcon} size='extra-tiny' />
                            </>
                        ) : (
                            t('export').toString()
                        )}
                    </Button>
                </div>
                <div
                    role='tabpanel'
                    className={styles.tabPanel}
                    aria-labelledby={accountAmountTabId}
                    hidden={drillDownState.activeTabIndex !== 0}
                >
                    <AccountAmountTabPanel
                        isUnitTestExecution={props.isUnitTestExecution}
                        key={`AccountAmountTabPanel ${drillDownCtxData.traceCalculationData[whitePaperCellDataRowIndex].displayRowNumber}`}
                        isFetchingOrSavingData={isFetchingOrSavingData}
                        isDataLoaded={isDataLoaded}
                        selectedCell={selectedCell}
                        onAmountCellValueChange={onAmountCellValueChange}
                        updateDrillDownState={updateDrillDownState}
                        changeTrackingData={props.changeTrackingData}
                        whitePaperReportColumn={props.whitePaperReportColumn!}
                        taxReturnKey={props.report.id!}
                        report={props.report}
                        isAmountDataEntryModifyEnabled={functionalAccess?.amountDataEntryModifyEnabled!}
                        onJournalEntryClick={onJournalEntryClick}
                        journalEntryButtonDisplayState={
                            isJournalEntryEnabled ? (functionalAccess?.journalEntryAccessEnabled ? 'enabled' : 'disabled') : 'invisible'
                        }
                        onEnterAmountsClick={onEnterAmountsClick}
                        rows={rows}
                        onOpenChange={onAccountAmountExpandChange}
                        openItems={drillDownState.openAccountSummaryItems}
                        updatedChangeTrackingDataMap={drillDownState.updatedChangeTrackingDataMap}
                    />
                </div>
                <div
                    role='tabpanel'
                    className={styles.tabPanel}
                    aria-labelledby={traceCalculationTabId}
                    hidden={drillDownState.activeTabIndex !== 1}
                >
                    {traceCalculationTabPanel}
                </div>
            </div>
            {showConfirmationDialogOnExit && showConfirmationDialog && (
                <ConfirmationDialogV9
                    title={t('saveChanges')}
                    content={t('preventNavigationDialogContent')}
                    buttons={
                        <>
                            <DialogTrigger>
                                <Button
                                    onClick={confirmationDialogOnDismissHandler}
                                    data-analytics-button-name='confirmation_dialog_cancel'
                                    aria-label={t('cancel').toString()}
                                >
                                    {t('cancel')}
                                </Button>
                            </DialogTrigger>
                            <Button
                                appearance='primary'
                                onClick={onConfirmationModalSubmit}
                                data-analytics-button-name='confirmation_dialog_save'
                                aria-label={t('saveChanges').toString()}
                            >
                                {t('saveChanges')}
                            </Button>
                            <Button
                                onClick={confirmationDialogCancelButtonHandler}
                                data-analytics-button-name='confirmation_dialog_discard'
                                aria-label={t('exitWithoutSaving').toString()}
                            >
                                {t('exitWithoutSaving')}
                            </Button>
                        </>
                    }
                />
            )}

            {drillDownState.enterAmountsDialogProps && (
                <EnterAmountsDialog
                    drillDownRowNumber={Number.parseInt(selectedCell.displayRowNumber!)}
                    columnId={props.whitePaperReportColumn?.originalColumnId!}
                    changeTrackingData={drillDownState.changeTrackingData}
                    taxReturnKey={props.report.id!}
                    rowDescription={
                        showEnterAmountsRowDescription
                            ? `Row ${selectedCell.description}, ${props.whitePaperReportColumn?.entityCode} ${props.whitePaperReportColumn?.entityName} `
                            : ''
                    }
                    onClose={onEnterAmountsClosedCallback}
                    {...drillDownState.enterAmountsDialogProps}
                />
            )}
            {isJournalEntryEnabled && drillDownState.journalEntryDialogProps && (
                <JournalEntryDialog {...drillDownState.journalEntryDialogProps} />
            )}
        </ToggleWidthDrawer>
    );
};

export default withAITracking(applicationInsightsReactPlugin, ReportDrillDown, 'Report Drill Down');
